<template>
  <transition name="expand" :css="isAnimated" @enter="enter" @after-enter="afterEnter" @leave="leave">
    <div v-if="shouldRenderContent" class="expand-wrapper">
      <div ref="visualContentRef" class="visual-content">
        <slot />
      </div>
    </div>
  </transition>
</template>

<script setup>
import { ref, defineExpose } from 'vue';
import { useEventListener } from '@vueuse/core';

const props = defineProps({
  shouldRenderContent: {
    type: Boolean,
    required: true,
  },
  isAnimated: {
    type: Boolean,
    default: false,
  },
});

const visualContentRef = ref(null);

function enter(el, done) {
  if (!props.isAnimated) {
    done();
    return;
  }

  el.style.height = '0';
  getComputedStyle(el);

  requestAnimationFrame(() => {
    if (visualContentRef.value) {
      const height = visualContentRef.value?.offsetHeight || 0;
      el.style.height = `${height}px`;
    }
  });

  useEventListener(el, 'transitionend', done, { once: true });
}

function afterEnter(el) {
  if (!props.isAnimated) return;

  el.style.height = 'auto';
}

function leave(el, done) {
  if (!props.isAnimated) {
    done();
    return;
  }

  const height = el.scrollHeight;
  el.style.height = `${height}px`;
  getComputedStyle(el);

  requestAnimationFrame(() => {
    el.style.height = '0';
  });

  useEventListener(el, 'transitionend', done, { once: true });
}

defineExpose({
  enter,
  afterEnter,
  leave,
});
</script>

<style scoped>
.expand-wrapper {
  overflow: hidden;
  transition: height 300ms ease-out 50ms;
}

.visual-content {
  transition:
    transform 300ms ease-out 1ms,
    opacity 300ms ease-out 1ms;
}

.expand-enter-from .visual-content,
.expand-leave-to .visual-content {
  transform: translateY(-150px);
  opacity: 0;
}

.expand-enter-to .visual-content,
.expand-leave-from .visual-content {
  transform: translateY(0);
  opacity: 1;
}
</style>
